/**
 * useDefaultTabCrud - Composable per gestione CRUD in tab default
 *
 * Gestisce il pattern ripetuto in tutti i *DefaultTab:
 * - Load entity by ID (edit mode)
 * - Create/Update con validation
 * - Dirty tracking automatico con snapshot
 * - Loading states e error handling
 */

import { ref, computed, watch, onMounted } from 'vue';
import type { Ref } from 'vue';
import { useRoute, useRouter } from '@/composables/useNuxtRouter';
import { useNotification } from '@/composables/useNotification';
import type { EntityKey } from '@/lib/constants/entityPermissions';
import { getEntityPermissions } from '@/lib/constants/entityPermissions';

/**
 * Genera i messaggi standard per un'entità
 * Formato neutro senza articoli: "{displayName}: Messaggio"
 */
function generateEntityMessages(entityKey: EntityKey) {
	const { displayName } = getEntityPermissions(entityKey);

	return {
		createSuccess: `${displayName}: Creazione avvenuta con successo`,
		updateSuccess: `${displayName}: Aggiornamento avvenuto con successo`,
		loadError: `${displayName}: Errore durante il caricamento!`,
		saveError: `${displayName}: Errore durante il salvataggio!`,
	};
}

interface UseDefaultTabCrudOptions<T> {
	// Chiave dell'entità per generazione automatica messaggi (OBBLIGATORIO)
	entityKey: EntityKey;

	// Servizi CRUD (quello che cambia tra entità)
	getById: (id: number | string) => Promise<T>;
	add: (data: T) => Promise<T>;
	update: (id: number | string, data: T) => Promise<T>;

	// Form data iniziale
	initialData: Partial<T>;

	// Ref del form Element UI (per validation)
	formRef: Ref<any>;

	// Dependencies da composables esistenti
	withLoading: (fn: () => Promise<void>) => Promise<void>;
	setAsDirty: () => void;
	setAsPristine: () => void;
	isDirty: Ref<boolean>; // Per controllo dirty state

	// Callback opzionale dopo load
	onDataLoaded?: (data: T) => void | Promise<void>;

	// Callback opzionale dopo save (permette override del comportamento default di navigazione)
	afterSave?: (isUpdate: boolean, savedData?: T) => void | Promise<void>;
}

export function useDefaultTabCrud<T extends Record<string, any>>(
	options: UseDefaultTabCrudOptions<T>,
) {
	const route = useRoute();
	const router = useRouter();
	const { success, error } = useNotification();

	// Genera messaggi automatici dall'entityKey
	const messages = generateEntityMessages(options.entityKey);

	// State
	const form = ref<T>(options.initialData as T) as Ref<T>;
	const formSnapshot = ref<string>('');

	// Computed
	const entityId = computed(() => {
		if (!route.params.id) {
			return null;
		}
		// Try to parse as number, if fails return as string (for GUIDs)
		const asNumber = Number(route.params.id);
		return isNaN(asNumber) ? (route.params.id as string) : asNumber;
	});

	/**
	 * Load entity data (edit mode only)
	 */
	async function loadData() {
		if (!entityId.value) {
			return;
		}

		await options.withLoading(async () => {
			try {
				const data = await options.getById(entityId.value!);
				form.value = data;

				// Snapshot per dirty tracking
				formSnapshot.value = JSON.stringify(data);

				options.setAsPristine();

				// Callback opzionale per logica extra
				if (options.onDataLoaded) {
					await options.onDataLoaded(data);
				}
			} catch (e) {
				console.error('❌ ERROR useDefaultTabCrud.loadData:', e);
				error(messages.loadError);
				router.back();
			}
		});
	}

	/**
	 * Save data (create or update)
	 */
	async function save() {
		// Early exit se non dirty
		if (!options.isDirty.value) {
			return;
		}

		// Validate form
		try {
			await options.formRef.value.validate();
		} catch (e) {
			console.error('❌ ERROR useDefaultTabCrud.save - Validation failed:', e);
			return;
		}

		await options.withLoading(async () => {
			try {
				const isUpdate = !!entityId.value;
				let savedData: T | undefined;

				if (isUpdate) {
					// UPDATE
					await options.update(entityId.value!, form.value);
					success(messages.updateSuccess);

					// Update snapshot dopo save
					formSnapshot.value = JSON.stringify(form.value);
					options.setAsPristine();
					savedData = form.value;
				} else {
					// CREATE
					const created = await options.add(form.value);
					success(messages.createSuccess);
					savedData = created;
				}

				// Se è stata fornita una callback afterSave, la esegue
				if (options.afterSave) {
					await options.afterSave(isUpdate, savedData);
					return;
				}

				// Comportamento default se nessuna callback
				if (isUpdate) {
					router.back();
				} else {
					// Se create ritorna l'entità con ID, naviga al dettaglio
					if (savedData && (savedData as any).id) {
						const basePath = route.path.replace('/add', '');
						router.push(`${basePath}/${(savedData as any).id}`);
						return;
					}

					router.back();
				}
			} catch (e) {
				console.error('❌ ERROR useDefaultTabCrud.save:', e);
				error(messages.saveError);
			}
		});
	}

	/**
	 * Dirty tracking automatico con snapshot
	 * Confronta lo stato corrente con lo snapshot dopo il load
	 */
	watch(
		form,
		(newValue) => {
			// Skip se non abbiamo ancora uno snapshot (primo caricamento)
			if (!formSnapshot.value) {
				return;
			}

			const current = JSON.stringify(newValue);
			if (current !== formSnapshot.value) {
				options.setAsDirty();
			} else {
				options.setAsPristine();
			}
		},
		{ deep: true },
	);

	// Load al mount se in edit mode
	onMounted(() => {
		if (entityId.value) {
			// Edit mode: carica i dati
			loadData();
		} else {
			// Create mode: inizializza snapshot con dati iniziali
			formSnapshot.value = JSON.stringify(options.initialData);
		}
	});

	return {
		form,
		save,
		loadData,
	};
}
